import { memo, useCallback, useRef } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { Button, type ButtonIntent } from '../Button.js';
import { DialogActions } from '../DialogActions.js';
import styles from './ConfirmDialog.module.css';

export type ConfirmDialogVariant = 'default' | 'destructive' | 'warning';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: React.ReactNode;
  readonly warning?: React.ReactNode;
  readonly variant?: ConfirmDialogVariant;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly confirmDisabled?: boolean;
  readonly isConfirming?: boolean;
  readonly disableCancelWhileConfirming?: boolean;
  readonly children?: React.ReactNode;
  /**
   * Content `data-testid`. Default `confirm-dialog`.
   * TP04 capture / hermetic SSOT: `admin-confirm-dialog` (wired via confirmApi).
   */
  readonly testId?: string;
}

function confirmIntent(variant: ConfirmDialogVariant): ButtonIntent {
  if (variant === 'destructive') {
    return 'danger';
  }
  return 'primary';
}

export const ConfirmDialog = memo<ConfirmDialogProps>(({
  open,
  onOpenChange,
  title,
  description,
  warning,
  variant = 'default',
  confirmLabel,
  cancelLabel,
  onConfirm,
  confirmDisabled = false,
  isConfirming = false,
  disableCancelWhileConfirming = false,
  children,
  testId = 'confirm-dialog',
}) => {
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const handleConfirm = useCallback((): void => {
    onConfirm();
  }, [onConfirm]);

  const handleOpenChange = useCallback((nextOpen: boolean): void => {
    if (!nextOpen && isConfirming) {
      return;
    }
    onOpenChange(nextOpen);
  }, [isConfirming, onOpenChange]);

  const handleEscapeKeyDown = useCallback((event: KeyboardEvent): void => {
    if (isConfirming) {
      event.preventDefault();
    }
  }, [isConfirming]);

  return (
    <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={styles.overlay} data-testid={`${testId}-overlay`} />
        <AlertDialog.Content
          className={styles.content}
          data-testid={testId}
          data-confirm-busy={isConfirming ? 'true' : undefined}
          aria-busy={isConfirming || undefined}
          aria-describedby={description !== undefined ? `${testId}-description` : undefined}
          onEscapeKeyDown={handleEscapeKeyDown}
          onOpenAutoFocus={(event) => {
            if (variant === 'destructive' || variant === 'warning') {
              event.preventDefault();
              cancelButtonRef.current?.focus();
            }
          }}
        >
          <header className={styles.header}>
            <AlertDialog.Title className={styles.title}>{title}</AlertDialog.Title>
          </header>
          <div className={styles.body}>
            {description !== undefined ? (
              <AlertDialog.Description
                id={`${testId}-description`}
                className={styles.description}
                asChild={typeof description !== 'string'}
              >
                {typeof description === 'string' ? description : <div>{description}</div>}
              </AlertDialog.Description>
            ) : null}
            {warning !== undefined ? (
              <p className={styles.warning}>{warning}</p>
            ) : null}
            {children}
          </div>
          <DialogActions testId={`${testId}-actions`}>
            <AlertDialog.Cancel asChild>
              <Button
                ref={cancelButtonRef}
                type="button"
                intent="secondary"
                size="md"
                disabled={isConfirming ? disableCancelWhileConfirming : false}
              >
                {cancelLabel}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button
                type="button"
                intent={confirmIntent(variant)}
                size="md"
                loading={isConfirming}
                disabled={confirmDisabled}
                className={variant === 'warning' ? styles.confirmButtonWarning : undefined}
                onClick={(event) => {
                  // Keep user activation for callers that open <input type="file">.
                  event.preventDefault();
                  handleConfirm();
                }}
                data-testid={`${testId}-confirm`}
              >
                {confirmLabel}
              </Button>
            </AlertDialog.Action>
          </DialogActions>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
});

ConfirmDialog.displayName = 'ConfirmDialog';
